import type { Core, Data } from '@strapi/types';
import { useMutation } from '@tanstack/react-query';

import { useAppContext } from '@/modules/app-context/use-app-context';
import { useApi } from '@/modules/utils/custom-hooks/use-api';
import { getErrorMessage } from '@/modules/utils/get-error-message';

import { useCheckIfThereIsUnacceptedAccesses } from './use-check-shared-access';
import {
	acceptAccessApi,
	createAccessApi,
	getLastEditorNameBySessionApi,
	leaveSharedDocumentApi,
	revokeAccessApi,
	updateAccessLevelApi,
	updateAccessPeriodApi,
} from '../api/shared-documents.api';

export const useSharedDocumentsApi = (projectDocumentId?: Data.Entity<'api::project-document.project-document'>['documentId']) => {
	const { showToast } = useAppContext();
	const { setIsUnacceptedAccesses } = useCheckIfThereIsUnacceptedAccesses();
	const { onSuccess } = useApi({ queryKey: ['GET_SHARED_PROJECT_DOCUMENTS_LIST'] });
	const { onSuccess: onSuccessGetInvitations } = useApi({ queryKey: ['GET_INVITATIONS'] });
	const { onSuccess: onSuccessGetDocumentAccess } = useApi({
		queryKey: ['GET_PROJECT_DOCUMENT_ACCESSES', projectDocumentId],
		closeModal: false,
	});

	const onError = (error: Error) => showToast({ message: getErrorMessage(error), variant: 'error' });
	const onSuccessHandler = () => {
		onSuccessGetInvitations();
		setIsUnacceptedAccesses(false);
		onSuccess();
	};

	const createAccess = useMutation({
		mutationKey: ['[POST]:CREATE_DOCUMENT_ACCESS'],
		mutationFn: ({
			documentDocumentId,
			body,
		}: {
			documentDocumentId: Data.Entity<'api::project-document.project-document'>['documentId'];
			body: Core.Helpers.Controllers.GetRequestBody<'api::document-access.document-access', 'create'>;
		}) => createAccessApi({ documentDocumentId, body }),
		onSuccess: () => onSuccessGetDocumentAccess(),
		onError,
	});

	const acceptAccess = useMutation({
		mutationKey: ['[POST]:ACCEPT_DOCUMENT_ACCESS'],
		mutationFn: ({ accessDocumentId }: { accessDocumentId: Data.Entity<'api::document-access.document-access'>['documentId'] }) =>
			acceptAccessApi({ accessDocumentId }),
		onSuccess: onSuccessHandler,
		onError,
	});

	const updateAccessLevel = useMutation({
		mutationKey: ['[POST]:UPDATE_DOCUMENT_ACCESS_LEVEL'],
		mutationFn: ({
			accessDocumentId,
			body,
		}: {
			accessDocumentId: Data.Entity<'api::document-access.document-access'>['documentId'];
			body: Core.Helpers.Controllers.GetRequestBody<'api::document-access.document-access', 'updateAccessLevel'>;
		}) => updateAccessLevelApi({ accessDocumentId, body }),
		onSuccess: () => onSuccessGetDocumentAccess(),
		onError,
	});

	const updateAccessPeriod = useMutation({
		mutationKey: ['[POST]:UPDATE_DOCUMENT_ACCESS_PERIOD'],
		mutationFn: ({
			accessDocumentId,
			body,
		}: {
			accessDocumentId: Data.Entity<'api::document-access.document-access'>['documentId'];
			body: Core.Helpers.Controllers.GetRequestBody<'api::document-access.document-access', 'updatePeriod'>;
		}) => updateAccessPeriodApi({ accessDocumentId, body }),
		onSuccess: () => onSuccessGetDocumentAccess(),
		onError,
	});

	const revokeAccess = useMutation({
		mutationKey: ['[DELETE]:REVOKE_DOCUMENT_ACCESS'],
		mutationFn: ({ accessDocumentId }: { accessDocumentId: Data.Entity<'api::document-access.document-access'>['documentId'] }) =>
			revokeAccessApi({ accessDocumentId }),
		onSuccess: () => onSuccessGetDocumentAccess(),
		onError,
	});

	const leaveSharedDocument = useMutation({
		mutationKey: ['[DELETE]:LEAVE_SHARED_DOCUMENT'],
		mutationFn: ({ accessDocumentId }: { accessDocumentId: Data.Entity<'api::document-access.document-access'>['documentId'] }) =>
			leaveSharedDocumentApi({ accessDocumentId }),
		onSuccess: onSuccessHandler,
		onError,
	});

	const getLastEditorNameBySession = useMutation({
		mutationKey: ['[POST]:CHECK_IF_DOCUMENT_SESSION_IS_CREATED'],
		mutationFn: ({ documentId }: { documentId: Data.Entity<'api::project-document.project-document'>['documentId'] }) =>
			getLastEditorNameBySessionApi({ documentId }),
		onError,
	});

	const isPending =
		createAccess.isPending ||
		acceptAccess.isPending ||
		updateAccessLevel.isPending ||
		updateAccessPeriod.isPending ||
		revokeAccess.isPending ||
		leaveSharedDocument.isPending ||
		getLastEditorNameBySession.isPending;

	return {
		createAccess,
		acceptAccess,
		updateAccessLevel,
		updateAccessPeriod,
		revokeAccess,
		leaveSharedDocument,
		getLastEditorNameBySession,
		isPending,
	};
};
